import type { CSSProperties } from 'react';

import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { useAnchorable } from '../../hooks/useAnchorable';

import { appendProtocol } from '../../utils/link';

import { container, image, imageLinkWrapper, sponsorText } from './AdvertisementOpportunityLogo.css';
import type { TPostAdvertisementOpportunityLogoProps } from './AdvertisementOpportunityLogo.types';

const DEFAULT_IMAGE_WIDTH = 300;

const IMAGE_ALIGNMENT_MARGINS = {
  left: '0 auto 0 0',
  center: '0 auto 0 auto',
  right: '0 0 0 auto',
} as const;

const CONTAINER_ALIGNMENT = {
  left: 'start',
  center: 'center',
  start: 'start',
  end: 'end',
  stretch: 'stretch',
};

export const PostAdvertisementOpportunityLogo = ({
  element,
  rewriteImageSrc,
}: TPostAdvertisementOpportunityLogoProps) => {
  const { attrs } = element;
  const imageSrc = attrs?.src && rewriteImageSrc ? rewriteImageSrc(attrs.src) : attrs?.src;
  const { anchorAttributes } = useAnchorable(element.attrs);
  const { utmParams } = usePostContentContext();
  const urlWithUTMParams = evaluateAndAppendUTMParams(appendProtocol(attrs?.url), utmParams);

  const shouldRender = !!attrs?.url && !!attrs?.src;

  if (!shouldRender) {
    return null;
  }

  const imageAlignmentMargins = IMAGE_ALIGNMENT_MARGINS[attrs.align || 'center'];
  const imageWidth = `${attrs.width ? (attrs.width / 100) * DEFAULT_IMAGE_WIDTH : DEFAULT_IMAGE_WIDTH}px`;

  return (
    <div
      {...anchorAttributes}
      style={
        {
          alignItems: attrs.align ? CONTAINER_ALIGNMENT[attrs.align] : 'center',
          margin: imageAlignmentMargins,
          '--image-width': imageWidth,
          '--image-alignment-margins': imageAlignmentMargins,
        } as CSSProperties
      }
      className={container}
    >
      <p
        className={sponsorText}
        style={{
          textAlign: attrs.align || 'center',
          paddingTop: 'var(--wt-paragraph-padding-top)',
          paddingBottom: 'var(--wt-paragraph-padding-bottom)',
        }}
      >
        <b>{attrs.sponsor_text}</b>
      </p>
      <a href={urlWithUTMParams} rel="nofollow noopener noreferrer" className={imageLinkWrapper} target="_blank">
        <img src={imageSrc} className={image} alt="" />
      </a>
    </div>
  );
};
